export interface FormField {
  id: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'textarea';
  required: boolean;
  options?: string[];
  placeholder?: string;
}

export interface FormResponse {
  id: string;
  form_id: string;
  participant_id: string;
  display_name: string;
  submitted_at: string;
  values: Record<string, string>;
}

export interface MeetingForm {
  id: string;
  title: string;
  description: string;
  is_active: boolean;
  max_submissions: number; // 1 = once per user, 0 = unlimited, N = custom
  fields: FormField[];
  created_at: string;
  responses: FormResponse[];
}

export class FormsDrawer {
  private isVisible = false;
  private containerEl: HTMLElement | null = null;
  private currentUserId = '';
  private currentUserRole = 'participant';
  private currentUserName = '';
  private roomId = '';

  private activeForm: MeetingForm | null = null;
  private userSubmissionsCount = 0;

  public onPublishForm?: (form: {
    title: string;
    description: string;
    max_submissions: number;
    fields: FormField[];
  }) => void;
  public onSubmitResponse?: (formId: string, values: Record<string, string>) => void;
  public onCloseForm?: (formId: string) => void;

  constructor(roomId: string) {
    this.roomId = roomId;
  }

  public getIsOpen(): boolean {
    return this.isVisible;
  }

  public setCurrentUser(id: string, role: string, name: string) {
    this.currentUserId = id;
    this.currentUserRole = role;
    this.currentUserName = name;
  }

  public setActiveForm(form: MeetingForm | null) {
    this.activeForm = form;
    this.updateUserSubmissionCount();
    if (this.isVisible && this.containerEl) {
      this.render(this.containerEl);
    }
  }

  public addResponse(resp: FormResponse) {
    if (this.activeForm && this.activeForm.id === resp.form_id) {
      const exists = this.activeForm.responses.some(r => r.id === resp.id);
      if (!exists) {
        this.activeForm.responses.unshift(resp);
      }
      this.updateUserSubmissionCount();
      if (this.isVisible && this.containerEl) {
        this.render(this.containerEl);
      }
    }
  }

  private updateUserSubmissionCount() {
    if (!this.activeForm || !this.activeForm.responses) {
      this.userSubmissionsCount = 0;
      return;
    }
    this.userSubmissionsCount = this.activeForm.responses.filter(
      r => r.participant_id === this.currentUserId
    ).length;
  }

  public toggle(container: HTMLElement) {
    this.isVisible = !this.isVisible;
    if (this.isVisible) {
      this.render(container);
    } else {
      this.hide();
    }
  }

  public hide() {
    this.isVisible = false;
    if (this.containerEl) {
      this.containerEl.innerHTML = '';
    }
  }

  public render(container: HTMLElement) {
    this.containerEl = container;
    const isHostOrMod = this.currentUserRole === 'host' || this.currentUserRole === 'moderator';

    container.innerHTML = `
      <div class="side-drawer" style="display: flex; flex-direction: column; height: 100%; width: 380px; max-width: 100vw; background: var(--bg-surface); border-left: 1px solid var(--border-subtle); box-shadow: var(--shadow-xl); z-index: 50;">
        <!-- Header -->
        <div style="padding: 1rem; border-bottom: 1px solid var(--border-subtle); display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 1.25rem;">📋</span>
            <div>
              <h3 style="font-size: 1rem; font-weight: 700; margin: 0;">Roll Call & Forms</h3>
              <p style="font-size: 0.75rem; color: var(--text-muted); margin: 0;">
                ${this.activeForm && this.activeForm.is_active ? 'Active Submission Open' : 'Attendance & Data Collection'}
              </p>
            </div>
          </div>
          <button id="btn-close-forms" class="control-btn" style="width: 32px; height: 32px;" title="Close">✕</button>
        </div>

        <!-- Body -->
        <div style="flex: 1; overflow-y: auto; padding: 1rem; display: flex; flex-direction: column; gap: 1rem;">
          ${isHostOrMod ? this.renderHostView() : this.renderParticipantView()}
        </div>
      </div>
    `;

    this.bindEvents(container);
  }

  private renderHostView(): string {
    if (!this.activeForm || !this.activeForm.is_active) {
      // Host: Form Creation / Launch Quick Roll Call
      return `
        <div style="display: flex; flex-direction: column; gap: 14px;">
          <!-- Quick Roll Call Template -->
          <div style="background: rgba(99, 102, 241, 0.08); border: 1px solid rgba(99, 102, 241, 0.25); border-radius: var(--radius-lg); padding: 1rem;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
              <span style="font-size: 1.1rem;">⏱️</span>
              <h4 style="font-size: 0.95rem; font-weight: 700; margin: 0;">Quick Attendance Roll Call</h4>
            </div>
            <p style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 12px;">
              Launch a 1-click attendance check with Full Name, Student/Employee ID, Status (Present/Late), and Notes. 1 submission per participant.
            </p>
            <button id="btn-launch-quick-rollcall" class="btn-primary" style="width: 100%; padding: 8px 14px; font-size: 0.85rem;">
              🚀 Launch Quick Roll Call
            </button>
          </div>

          <!-- Custom Form Creator -->
          <div style="border-top: 1px solid var(--border-subtle); padding-top: 14px;">
            <h4 style="font-size: 0.95rem; font-weight: 700; margin-bottom: 10px;">Custom Form / Survey</h4>
            
            <div style="display: flex; flex-direction: column; gap: 10px;">
              <div>
                <label style="font-size: 0.75rem; color: var(--text-muted); display: block; margin-bottom: 4px;">Form Title</label>
                <input id="input-form-title" class="input-text" type="text" placeholder="e.g. Session Feedback, Sign-in Sheet" style="width: 100%;" />
              </div>

              <div>
                <label style="font-size: 0.75rem; color: var(--text-muted); display: block; margin-bottom: 4px;">Submission Limit</label>
                <select id="select-max-submissions" class="input-text" style="width: 100%;">
                  <option value="1" selected>Single submission only (1 per user)</option>
                  <option value="0">Multiple submissions allowed (Unlimited)</option>
                  <option value="2">Up to 2 submissions per user</option>
                  <option value="3">Up to 3 submissions per user</option>
                </select>
              </div>

              <button id="btn-create-custom-form" class="btn-secondary" style="width: 100%; padding: 8px 14px; font-size: 0.85rem;">
                ➕ Create & Publish Form
              </button>
            </div>
          </div>
        </div>
      `;
    }

    // Host: Active Form with Live Responses Table & Download
    const form = this.activeForm;
    const respCount = form.responses ? form.responses.length : 0;

    return `
      <div style="display: flex; flex-direction: column; gap: 14px;">
        <!-- Status Banner -->
        <div style="background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.3); border-radius: var(--radius-md); padding: 10px 12px; display: flex; justify-content: space-between; align-items: center;">
          <div>
            <div style="font-size: 0.9rem; font-weight: 700; color: #34d399;">${form.title}</div>
            <div style="font-size: 0.75rem; color: var(--text-muted);">
              ${respCount} ${respCount === 1 ? 'response' : 'responses'} received • Limit: ${form.max_submissions === 1 ? '1 per user' : (form.max_submissions === 0 ? 'Unlimited' : `${form.max_submissions} per user`)}
            </div>
          </div>
          <button id="btn-host-close-form" class="btn-danger" style="padding: 4px 10px; font-size: 0.75rem;">
            Close Form
          </button>
        </div>

        <!-- Export / Download Action Buttons -->
        <div>
          <div style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); margin-bottom: 6px; text-transform: uppercase;">
            Download Responses
          </div>
          <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px;">
            <button id="btn-dl-csv" class="btn-secondary" style="padding: 6px 8px; font-size: 0.75rem; display: flex; align-items: center; justify-content: center; gap: 4px;" title="Export RFC-4180 CSV">
              📄 CSV
            </button>
            <button id="btn-dl-excel" class="btn-secondary" style="padding: 6px 8px; font-size: 0.75rem; display: flex; align-items: center; justify-content: center; gap: 4px;" title="Export Excel Compatible File">
              📊 Excel
            </button>
            <button id="btn-dl-txt" class="btn-secondary" style="padding: 6px 8px; font-size: 0.75rem; display: flex; align-items: center; justify-content: center; gap: 4px;" title="Export Plain Text Report">
              📝 TXT
            </button>
          </div>
        </div>

        <!-- Live Submissions List -->
        <div>
          <div style="font-size: 0.75rem; font-weight: 700; color: var(--text-muted); margin-bottom: 6px; text-transform: uppercase;">
            Live Response Log
          </div>
          ${respCount === 0 ? `
            <div style="text-align: center; padding: 1.5rem; color: var(--text-muted); font-size: 0.85rem; background: var(--bg-surface-elevated); border-radius: var(--radius-md);">
              Waiting for participant responses...
            </div>
          ` : `
            <div style="display: flex; flex-direction: column; gap: 8px; max-height: 380px; overflow-y: auto;">
              ${form.responses.map(r => this.renderResponseItem(r, form.fields)).join('')}
            </div>
          `}
        </div>
      </div>
    `;
  }

  private renderParticipantView(): string {
    if (!this.activeForm || !this.activeForm.is_active) {
      return `
        <div style="text-align: center; padding: 2rem 1rem; color: var(--text-muted);">
          <div style="font-size: 2.5rem; margin-bottom: 10px;">📋</div>
          <h4 style="font-size: 1rem; font-weight: 600; color: var(--text-primary); margin-bottom: 6px;">No Active Form</h4>
          <p style="font-size: 0.825rem; line-height: 1.4;">
            When the host starts an attendance roll call or publishes a form, it will appear here for you to submit.
          </p>
        </div>
      `;
    }

    const form = this.activeForm;
    const isLimitReached = form.max_submissions > 0 && this.userSubmissionsCount >= form.max_submissions;

    if (isLimitReached) {
      return `
        <div style="text-align: center; padding: 2rem 1rem;">
          <div style="font-size: 2.5rem; margin-bottom: 10px;">✅</div>
          <h4 style="font-size: 1.1rem; font-weight: 700; color: #34d399; margin-bottom: 6px;">Response Recorded</h4>
          <p style="font-size: 0.85rem; color: var(--text-secondary); margin-bottom: 1rem;">
            Thank you! Your submission for <strong>${form.title}</strong> has been saved.
          </p>
          <div style="background: var(--bg-surface-elevated); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 10px; font-size: 0.75rem; color: var(--text-muted);">
            Submission policy: ${form.max_submissions} submission per participant.
          </div>
        </div>
      `;
    }

    return `
      <form id="form-participant-submit" style="display: flex; flex-direction: column; gap: 12px;">
        <div style="margin-bottom: 4px;">
          <h4 style="font-size: 1rem; font-weight: 700; color: var(--text-primary); margin: 0 0 4px 0;">${form.title}</h4>
          ${form.description ? `<p style="font-size: 0.8rem; color: var(--text-secondary); margin: 0;">${form.description}</p>` : ''}
          <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 4px;">
            Submissions: ${this.userSubmissionsCount} of ${form.max_submissions === 0 ? 'unlimited' : form.max_submissions}
          </div>
        </div>

        ${form.fields.map(f => this.renderFieldInput(f)).join('')}

        <button type="submit" class="btn-primary" style="margin-top: 8px; width: 100%; padding: 10px;">
          Submit Response
        </button>
      </form>
    `;
  }

  private renderFieldInput(f: FormField): string {
    const defaultVal = f.id === 'name' ? this.currentUserName : '';

    if (f.type === 'select') {
      return `
        <div>
          <label style="font-size: 0.8rem; font-weight: 600; display: block; margin-bottom: 4px;">
            ${f.label} ${f.required ? '<span style="color: var(--color-danger);">*</span>' : ''}
          </label>
          <select name="${f.id}" class="input-text" style="width: 100%;" ${f.required ? 'required' : ''}>
            ${(f.options || ['Present', 'Late', 'Excused']).map(opt => `
              <option value="${opt}">${opt}</option>
            `).join('')}
          </select>
        </div>
      `;
    }

    if (f.type === 'textarea') {
      return `
        <div>
          <label style="font-size: 0.8rem; font-weight: 600; display: block; margin-bottom: 4px;">
            ${f.label} ${f.required ? '<span style="color: var(--color-danger);">*</span>' : ''}
          </label>
          <textarea name="${f.id}" class="input-text" rows="2" style="width: 100%; resize: vertical;" placeholder="${f.placeholder || ''}" ${f.required ? 'required' : ''}></textarea>
        </div>
      `;
    }

    return `
      <div>
        <label style="font-size: 0.8rem; font-weight: 600; display: block; margin-bottom: 4px;">
          ${f.label} ${f.required ? '<span style="color: var(--color-danger);">*</span>' : ''}
        </label>
        <input name="${f.id}" type="${f.type}" value="${defaultVal}" placeholder="${f.placeholder || ''}" class="input-text" style="width: 100%;" ${f.required ? 'required' : ''} />
      </div>
    `;
  }

  private renderResponseItem(r: FormResponse, fields: FormField[]): string {
    const timeStr = new Date(r.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    return `
      <div style="background: var(--bg-surface-elevated); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 10px; font-size: 0.8rem;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
          <span style="font-weight: 700; color: var(--text-primary);">${r.display_name}</span>
          <span style="font-size: 0.7rem; color: var(--text-muted); font-family: var(--font-mono);">${timeStr}</span>
        </div>
        <div style="display: flex; flex-direction: column; gap: 4px;">
          ${fields.map(f => {
            const val = r.values[f.id] || '—';
            return `
              <div style="display: flex; justify-content: space-between; gap: 8px;">
                <span style="color: var(--text-muted); font-size: 0.75rem;">${f.label}:</span>
                <span style="font-weight: 600; color: ${val === 'Present' ? '#34d399' : 'var(--text-primary)'};">${val}</span>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  private bindEvents(container: HTMLElement) {
    container.querySelector('#btn-close-forms')?.addEventListener('click', () => {
      this.hide();
    });

    // Host Quick Roll Call Launcher
    container.querySelector('#btn-launch-quick-rollcall')?.addEventListener('click', () => {
      if (this.onPublishForm) {
        this.onPublishForm({
          title: 'Class & Meeting Attendance Roll Call',
          description: 'Please submit your attendance record for this session.',
          max_submissions: 1, // Strict single submission per user
          fields: [
            { id: 'roll_id', label: 'Roll / Student / Employee ID', type: 'text', required: true, placeholder: `e.g. 101, ENG-${new Date().getFullYear()}` },
            { id: 'name', label: 'Full Legal Name', type: 'text', required: true, placeholder: 'Your Name' },
            { id: 'status', label: 'Attendance Status', type: 'select', required: true, options: ['Present', 'Late', 'Excused'] },
            { id: 'notes', label: 'Remarks / Comments', type: 'text', required: false, placeholder: 'Optional remarks' },
          ],
        });
      }
    });

    // Host Custom Form Launcher
    container.querySelector('#btn-create-custom-form')?.addEventListener('click', () => {
      const titleInput = container.querySelector('#input-form-title') as HTMLInputElement;
      const selectLimit = container.querySelector('#select-max-submissions') as HTMLSelectElement;

      const title = titleInput?.value.trim() || 'Session Questionnaire';
      const maxSubs = parseInt(selectLimit?.value || '1', 10);

      if (this.onPublishForm) {
        this.onPublishForm({
          title,
          description: 'Please provide your responses below.',
          max_submissions: maxSubs,
          fields: [
            { id: 'name', label: 'Participant Name', type: 'text', required: true },
            { id: 'feedback', label: 'Response / Feedback', type: 'textarea', required: true, placeholder: 'Enter your response...' },
          ],
        });
      }
    });

    // Host Close Form
    container.querySelector('#btn-host-close-form')?.addEventListener('click', () => {
      if (this.activeForm && confirm('Close this form? No further submissions will be accepted.')) {
        if (this.onCloseForm) this.onCloseForm(this.activeForm.id);
      }
    });

    // Export Handlers
    container.querySelector('#btn-dl-csv')?.addEventListener('click', () => this.downloadCSV());
    container.querySelector('#btn-dl-excel')?.addEventListener('click', () => this.downloadExcel());
    container.querySelector('#btn-dl-txt')?.addEventListener('click', () => this.downloadTXT());

    // Participant Submit Handler
    const submitForm = container.querySelector('#form-participant-submit') as HTMLFormElement;
    submitForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!this.activeForm) return;

      const formData = new FormData(submitForm);
      const values: Record<string, string> = {};
      this.activeForm.fields.forEach(f => {
        values[f.id] = (formData.get(f.id) as string) || '';
      });

      if (this.onSubmitResponse) {
        this.onSubmitResponse(this.activeForm.id, values);
      }
    });
  }

  public downloadCSV() {
    if (!this.activeForm || !this.activeForm.responses || this.activeForm.responses.length === 0) {
      alert('No responses recorded yet to download.');
      return;
    }

    const form = this.activeForm;
    const headerCols = ['Timestamp', 'Participant Name', 'Participant ID', ...form.fields.map(f => f.label)];
    const escapeCsv = (str: string) => `"${(str || '').replace(/"/g, '""')}"`;

    let csvContent = headerCols.map(escapeCsv).join(',') + '\r\n';

    for (const r of form.responses) {
      const row = [
        new Date(r.submitted_at).toISOString(),
        r.display_name,
        r.participant_id,
        ...form.fields.map(f => r.values[f.id] || ''),
      ];
      csvContent += row.map(escapeCsv).join(',') + '\r\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const filename = `verve-rollcall-${this.roomId}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  public downloadExcel() {
    if (!this.activeForm || !this.activeForm.responses || this.activeForm.responses.length === 0) {
      alert('No responses recorded yet to download.');
      return;
    }

    const form = this.activeForm;
    const headerCols = ['Timestamp', 'Participant Name', 'Participant ID', ...form.fields.map(f => f.label)];
    
    // Tab-delimited format with UTF-8 BOM for automatic Excel formatting
    let content = '\uFEFF' + headerCols.join('\t') + '\r\n';

    for (const r of form.responses) {
      const row = [
        new Date(r.submitted_at).toLocaleString(),
        r.display_name,
        r.participant_id,
        ...form.fields.map(f => (r.values[f.id] || '').replace(/[\t\r\n]/g, ' ')),
      ];
      content += row.join('\t') + '\r\n';
    }

    const blob = new Blob([content], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const filename = `verve-rollcall-${this.roomId}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.xls`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  public downloadTXT() {
    if (!this.activeForm || !this.activeForm.responses || this.activeForm.responses.length === 0) {
      alert('No responses recorded yet to download.');
      return;
    }

    const form = this.activeForm;
    let txt = `============================================================\n`;
    txt += `VERVE ATTENDANCE & ROLL CALL REPORT\n`;
    txt += `Meeting Room: ${this.roomId}\n`;
    txt += `Form Title:   ${form.title}\n`;
    txt += `Generated:    ${new Date().toLocaleString()}\n`;
    txt += `Total Count:  ${form.responses.length} responses\n`;
    txt += `============================================================\n\n`;

    form.responses.forEach((r, idx) => {
      txt += `[#${idx + 1}] ${r.display_name} (${new Date(r.submitted_at).toLocaleTimeString()})\n`;
      txt += `     User ID: ${r.participant_id}\n`;
      form.fields.forEach(f => {
        txt += `     ${f.label.padEnd(20)}: ${r.values[f.id] || '—'}\n`;
      });
      txt += `------------------------------------------------------------\n`;
    });

    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const filename = `verve-rollcall-${this.roomId}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.txt`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
